/**
 * AgenticPaymentOffers - Main Component
 *
 * Entry point for the Offers admin feature.
 * Fetches all offers via the Rails admin proxy endpoint.
 * All LP API calls are proxied through Rails, no LP API credentials in the browser.
 *
 * Status and search filtering is server-side via the admin endpoint.
 * Offer type and currency filtering remain client-side.
 *
 * @component
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import OffersTable from "./OffersTable";
import { ITEMS_PER_PAGE } from "./constants";
import { getOfferStatus } from "./utils";
import { pageHeaderStyles, colors } from "./styles";
import { getRequest } from "../../utils/httpRequest";
import "./styles.scss";

const AgenticPaymentOffers = () => {
  // ── Raw data ──────────────────────────────────────────────────────────
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Filters (client-side) ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [offerTypeFilter, setOfferTypeFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // ── Pagination ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── Fetch all offers via Rails proxy ──────────────────────────────────
  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getRequest(
        "/admin/agentic_payment_prediction_offers/list_offers?limit=500&page=1",
      );
      const payload = data.data || data;
      const list = Array.isArray(payload)
        ? payload
        : payload.offers || payload.data || [];
      setOffers(list);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError(err?.message || "Failed to load offers");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, offerTypeFilter, currencyFilter, statusFilter]);

  // ── Unique currency list (for dropdown) ───────────────────────────────
  const currencies = useMemo(() => {
    const set = new Set(offers.map((o) => o.currency).filter(Boolean));
    return Array.from(set).sort();
  }, [offers]);

  // ── Client-side filtering ─────────────────────────────────────────────
  const filteredOffers = useMemo(() => {
    let filtered = offers;

    if (offerTypeFilter !== "all") {
      filtered = filtered.filter((o) => o.offerType === offerTypeFilter);
    }

    if (currencyFilter !== "all") {
      filtered = filtered.filter(
        (o) => o.currency?.toUpperCase() === currencyFilter.toUpperCase(),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((o) => {
        const status = getOfferStatus(o).toLowerCase().replace(/ /g, "_");
        return status === statusFilter;
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.userEmail?.toLowerCase().includes(q) ||
          o.userName?.toLowerCase().includes(q) ||
          o.advertiser?.toLowerCase().includes(q) ||
          o.currency?.toLowerCase().includes(q) ||
          o.id?.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [offers, offerTypeFilter, currencyFilter, statusFilter, searchQuery]);

  // ── Client-side pagination ────────────────────────────────────────────
  const totalPages = Math.ceil(filteredOffers.length / ITEMS_PER_PAGE);
  const paginatedOffers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOffers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOffers, currentPage]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="liquidity-pool-offers">
      {/* Page Header */}
      <div style={pageHeaderStyles.container}>
        <h1 style={pageHeaderStyles.title}>Prediction Offers</h1>
        <p style={pageHeaderStyles.subtitle}>
          View and inspect all Currency Prediction offers on the liquidity pool
        </p>
      </div>

      {/* Content */}
      <div style={{ padding: "20px 0" }}>
        {error ? (
          <div
            className="flash flash_error"
            style={{ textAlign: "center", padding: "24px" }}
          >
            <p style={{ margin: "0 0 12px" }}>{error}</p>
            <button
              type="button"
              onClick={fetchOffers}
              style={{
                padding: "10px 24px",
                background: colors.primary,
                color: colors.white,
                border: "none",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <OffersTable
            offers={filteredOffers}
            allOffers={offers}
            paginatedOffers={paginatedOffers}
            loading={loading}
            totalCount={offers.length}
            currentPage={currentPage}
            totalPages={totalPages}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            offerTypeFilter={offerTypeFilter}
            onOfferTypeChange={setOfferTypeFilter}
            currencyFilter={currencyFilter}
            onCurrencyChange={setCurrencyFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            onPageChange={setCurrentPage}
            currencies={currencies}
            onRefresh={fetchOffers}
          />
        )}
      </div>
    </div>
  );
};

export default AgenticPaymentOffers;
